import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { reportApi, clientApi } from '@/services/api'
import type { ProfitLoss as ProfitLossType } from '@/types'
import { formatCurrency } from '@/utils/currency'
import { BarChart3, Download } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

export default function ProfitLoss() {
  const { clientId } = useParams<{ clientId: string }>()
  const [report, setReport] = useState<ProfitLossType | null>(null)
  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState(() => `${new Date().getFullYear()}-01-01`)
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0])
  const [exporting, setExporting] = useState(false)
  const [currency, setCurrency] = useState('USD')

  useEffect(() => {
    if (clientId) clientApi.get(clientId).then((r) => setCurrency(r.data.client.currency)).catch(() => {})
    generate()
  }, [clientId])

  async function generate() {
    if (!clientId) return
    setLoading(true)
    try {
      const res = await reportApi.profitLoss(clientId, startDate, endDate)
      setReport(res.data)
    } finally { setLoading(false) }
  }

  async function handleExport() {
    if (!clientId) return
    setExporting(true)
    try {
      const res = await reportApi.exportPdf(clientId, 'profit-loss', { start_date: startDate, end_date: endDate })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `profit-loss-${startDate}-to-${endDate}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Export failed')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Profit &amp; Loss</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Revenue and expenses for the period</p>
        </div>
        {report && (
          <Button variant="outline" onClick={handleExport} disabled={exporting}>
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label>From</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-auto" />
            </div>
            <div className="space-y-1.5">
              <Label>To</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-auto" />
            </div>
            <Button onClick={generate} disabled={loading}>
              {loading ? 'Generating...' : 'Generate'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="p-5 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </CardContent>
        </Card>
      ) : report ? (
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-muted/30">
            <p className="text-[13px] text-[rgba(0,10,30,0.75)]">{report.start_date} to {report.end_date}</p>
          </div>

          {/* Revenue */}
          <div className="border-b border-border">
            <div className="px-5 py-2.5 bg-emerald-50/50 border-b border-border">
              <p className="text-[13px] font-bold text-emerald-700 uppercase tracking-wider">Revenue</p>
            </div>
            <table className="w-full">
              <tbody>
                {report.revenue.accounts.map((acc) => (
                  <tr key={acc.account_id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="px-5 py-2.5 text-[13px] text-[rgba(0,10,30,0.75)] font-mono w-20">{acc.account_code}</td>
                    <td className="px-2 py-2.5 text-[13px] text-[#000A1E]">{acc.account_name}</td>
                    <td className="px-5 py-2.5 text-[13px] text-right tabular-nums text-[#000A1E]">{formatCurrency(acc.net, currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-emerald-50/30">
                  <td colSpan={2} className="px-5 py-3 text-[13px] font-bold text-[#000A1E]">Total Revenue</td>
                  <td className="px-5 py-3 text-[13px] font-bold text-right tabular-nums text-emerald-700">{formatCurrency(report.revenue.total, currency)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Expenses */}
          <div className="border-b border-border">
            <div className="px-5 py-2.5 bg-red-50/50 border-b border-border">
              <p className="text-[13px] font-bold text-red-700 uppercase tracking-wider">Expenses</p>
            </div>
            <table className="w-full">
              <tbody>
                {report.expenses.accounts.map((acc) => (
                  <tr key={acc.account_id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="px-5 py-2.5 text-[13px] text-[rgba(0,10,30,0.75)] font-mono w-20">{acc.account_code}</td>
                    <td className="px-2 py-2.5 text-[13px] text-[#000A1E]">{acc.account_name}</td>
                    <td className="px-5 py-2.5 text-[13px] text-right tabular-nums text-[#000A1E]">{formatCurrency(acc.net, currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-red-50/30">
                  <td colSpan={2} className="px-5 py-3 text-[13px] font-bold text-[#000A1E]">Total Expenses</td>
                  <td className="px-5 py-3 text-[13px] font-bold text-right tabular-nums text-red-700">{formatCurrency(report.expenses.total, currency)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Net Profit */}
          <div className="px-5 py-4 bg-muted/30 flex justify-between items-center">
            <span className="text-base font-bold text-[#000A1E]">Net Profit</span>
            <span className={`text-lg font-bold tabular-nums ${parseFloat(report.net_profit) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {formatCurrency(report.net_profit, currency)}
            </span>
          </div>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <BarChart3 className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
            <p className="text-[13px] text-[rgba(0,10,30,0.75)]">Select a date range and click Generate</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
